import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "./AuthContext";
import toast from 'react-hot-toast';

export interface ReleasesContextType {
  toggleListen: (releaseId: number, currentlyListened: boolean) => Promise<void>;
  isSubmitting: boolean;
  loadingReleaseId: number | null;
  error: string | null;
  // Returns the session toggle state, or undefined if not toggled this session
  getSessionToggle: (releaseId: number) => boolean | undefined;
}

export const ReleasesContext = createContext<ReleasesContextType>({
  toggleListen: async () => {},
  isSubmitting: false,
  loadingReleaseId: null,
  error: null,
  getSessionToggle: () => undefined
});

export const ReleasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId, isAuthenticated } = useAuth();
  // Track only session toggles - Map<releaseId, newListenedState>
  const [sessionToggles, setSessionToggles] = useState<Map<number, boolean>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingReleaseId, setLoadingReleaseId] = useState<number | null>(null);
  const [lastToggleTime, setLastToggleTime] = useState<number>(0);

  // Helper function to parse Supabase errors
  const getErrorMessage = (error: any): string => {
    // Check for rate limiting errors
    if (error?.message?.includes('rate limit') ||
        error?.message?.includes('too many requests') ||
        error?.code === '429' ||
        error?.status === 429) {
      return 'You\'re updating too quickly. Please wait a moment before trying again.';
    }

    // Check for network errors
    if (error?.message?.includes('network') ||
        error?.message?.includes('connection')) {
      return 'Network error. Please check your connection and try again.';
    }

    // Default error message
    return 'Failed to update. Please try again.';
  };

  // Get session toggle state for a release (undefined if not toggled this session)
  const getSessionToggle = useCallback((releaseId: number): boolean | undefined => {
    return sessionToggles.get(releaseId);
  }, [sessionToggles]);

  // Toggle listen status for a release
  const toggleListen = useCallback(async (releaseId: number, currentlyListened: boolean) => {
    if (!userId || !isAuthenticated || !releaseId) return;

    // Prevent clicking the same release that's currently loading
    if (loadingReleaseId === releaseId) {
      return;
    }

    // Check for rate limiting by enforcing a minimum interval between requests
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTime;

    // Apply rate limiting regardless of loading state
    if (timeSinceLastToggle < 300) {
      toast.error('Please slow down, you\'re toggling too fast!', {
        duration: 3000,
        position: 'bottom-center',
      });
      return;
    }

    // Determine new state (toggle from current)
    const newListenedState = !currentlyListened;

    // Store previous state for error recovery
    const previousToggles = new Map(sessionToggles);

    try {
      // Update local state first for immediate UI feedback
      const newToggles = new Map(sessionToggles);
      newToggles.set(releaseId, newListenedState);
      setSessionToggles(newToggles);

      // Set loading state
      setIsSubmitting(true);
      setLoadingReleaseId(releaseId);
      setLastToggleTime(now);
      setError(null);

      // Directly modify the database for just this one release
      const { error: dbError } = newListenedState
        ? await supabase
            .from('user_listened_releases')
            .upsert({ user_id: userId, release_id: releaseId }, { onConflict: 'user_id,release_id' })
        : await supabase
            .from('user_listened_releases')
            .delete()
            .match({ user_id: userId, release_id: releaseId });

      if (dbError) {
        throw dbError;
      }

      // Show success toast
      toast.success(newListenedState ? 'Marked as listened' : 'Removed from listened', {
        duration: 1500,
        position: 'bottom-center',
        style: {
          background: newListenedState ? '#10B981' : '#6B7280',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '8px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: newListenedState ? '#10B981' : '#6B7280',
        },
      });

    } catch (error) {
      console.error('Error updating listen status:', error);

      // Revert to previous state on error
      setSessionToggles(previousToggles);

      // Get specific error message
      const errorMessage = getErrorMessage(error);
      setError('Failed to update listen status');

      toast.error(errorMessage, {
        duration: 3000,
        position: 'bottom-center',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingReleaseId(null);
    }
  }, [userId, isAuthenticated, sessionToggles, loadingReleaseId, lastToggleTime]);

  // Memoize the context value
  const contextValue = useMemo(() => ({
    toggleListen,
    isSubmitting,
    loadingReleaseId,
    error,
    getSessionToggle
  }), [
    toggleListen,
    isSubmitting,
    loadingReleaseId,
    error,
    getSessionToggle
  ]);

  return (
    <ReleasesContext.Provider value={contextValue}>
      {children}
    </ReleasesContext.Provider>
  );
};

// Custom hook for consuming the releases context
export const useReleases = () => {
  const context = useContext(ReleasesContext);
  if (!context) {
    throw new Error("useReleases must be used within a ReleasesProvider");
  }
  return context;
};
